import BookLocation from 'types/BookLocation';
import getBookLocationPropertiesFromBackendData from '../getBookLocationPropertiesFromBackendData';
import getBookLocationPropertiesFromLocalSettings from '../getBookLocationPropertiesFromLocalSettings';

/**
 * Given a book and raw backend data, compares backend data and local data and determines the best place in the title to send the user.
 * It prefers whichever data source has a newer timestamp, and defaults to the beginning of the title if there is no data to consider.
 *
 * @param {Book} book - relevant book. the epubLocationHistoryData must match
 * @param {Object} epubLocationHistoryData - an object with backend data for consideration
 * @returns {BookLocation} the most recent book location from local data or from backend data
 */
const getMostRecentBookLocation = (book, epubLocationHistoryData) => {
  const bookLocationPropertiesFromLocalSettings =
    getBookLocationPropertiesFromLocalSettings(book);

  const bookLocationPropertiesFromBackendData =
    getBookLocationPropertiesFromBackendData(epubLocationHistoryData);

  if (bookLocationPropertiesFromBackendData) {
    // If epubLocationHistoryData is included, it should always correspond to the specifed book.
    if (epubLocationHistoryData.epub.pk !== book.pk) {
      throw new Error(
        `getMostRecentBookLocation expected epubLocationHistoryData for book with pk ${book.pk}, received ${epubLocationHistoryData.epub.pk}.`,
      );
    }

    if (bookLocationPropertiesFromLocalSettings) {
      // Both backend and local data are available.
      // Use whichever has a newer timestamp.
      const bookLocationProperties =
        bookLocationPropertiesFromBackendData.timestamp >=
        bookLocationPropertiesFromLocalSettings.timestamp
          ? bookLocationPropertiesFromBackendData
          : bookLocationPropertiesFromLocalSettings;

      return new BookLocation({
        book,
        ...bookLocationProperties,
      });
    }
  }

  // Use whatever data is available, or return a generic location.
  const bookLocationProperties =
    bookLocationPropertiesFromBackendData ??
    bookLocationPropertiesFromLocalSettings ??
    {};

  return new BookLocation({
    book,
    ...bookLocationProperties,
  });
};

export default getMostRecentBookLocation;
